import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { FaTimes, FaCheck, FaCamera } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { getUserProfile, updateUserProfile, uploadUserImage } from "../Services/client";
import FullPageLoader from "./FullPageLoader";
import "../css/login.css";

const Profile = ({ show, closeProfileForm, callback }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [userImgUrl, setUserImgUrl] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfile = async () => {
      const mobile_no = localStorage.getItem("userMobile");
      if (!mobile_no) return;

      setIsLoading(true);
      try {
        const response = await getUserProfile(mobile_no);
        if (response?.data?.success) {
          setName(response.data.profile?.name || "");
          setEmail(response.data.profile?.email || "");
          setUserImgUrl(response.data.profile?.user_img || null);
        }
      } catch (err) {
        // No existing profile yet — leave fields blank
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const validateProfile = () => {
    if (!name.trim()) {
      setError(t("name_required"));
      return false;
    }
    if (!email.trim()) {
      setError(t("email_required"));
      return false;
    }
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
      setError(t("invalid_email"));
      return false;
    }
    return true;
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show local preview immediately
    setPreviewImg(URL.createObjectURL(file));

    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setImgUploading(true);
    try {
      const res = await uploadUserImage(userId, file);
      if (res?.data?.success) {
        setUserImgUrl(res.data.user_img);
      }
    } catch (err) {
      console.error("Image upload failed", err);
    } finally {
      setImgUploading(false);
    }
  };

  const handleSave = async () => {
    setError(null);

    if (validateProfile()) {
      setIsLoading(true);
      const mobile_no = localStorage.getItem("userMobile");

      try {
        const response = await updateUserProfile(mobile_no, name, email);
        if (response === "Error" || response?.data?.success !== true) {
          setIsLoading(false);
          setError(response?.data?.message || "Failed to save profile");
          return;
        }

        localStorage.setItem("userName", name.trim());
        setSuccessMsg(t("profile_saved_message"));
        callback({ name: name.trim(), imgUrl: userImgUrl });
        setTimeout(() => {
          setIsLoading(false);
          closeProfileForm();
        }, 2000);
      } catch (err) {
        setIsLoading(false);
        setError(err.message);
      }
    }
  };

  const displayImg = previewImg || userImgUrl;

  return (
    <Modal show={show} onHide={closeProfileForm} centered size="md" backdrop="static" keyboard={false}>
      <Modal.Header closeButton className="modal-header-color">
        <Modal.Title className="cart-header">{t("profile")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Profile image circle */}
        <div className="d-flex justify-content-center mb-4">
          <div
            style={{ position: "relative", display: "inline-block", cursor: "pointer" }}
            onClick={() => fileInputRef.current?.click()}
          >
            {displayImg ? (
              <img
                src={displayImg}
                alt="Profile"
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid #28a745",
                }}
              />
            ) : (
              <div
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  background: "#e9f5ee",
                  border: "3px solid #28a745",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fas fa-user" style={{ fontSize: 36, color: "#28a745" }}></i>
              </div>
            )}
            {/* Camera overlay */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                right: 0,
                background: "#28a745",
                borderRadius: "50%",
                width: 26,
                height: 26,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imgUploading ? (
                <span style={{ color: "#fff", fontSize: 10 }}>...</span>
              ) : (
                <FaCamera size={13} color="#fff" />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
          </div>
        </div>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>{t("name")}</Form.Label>
            <Form.Control
              type="text"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("email")}</Form.Label>
            <Form.Control
              type="email"
              value={email}
              maxLength={100}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Form.Group>

          {error && <p className="text-danger">{error}</p>}
          {successMsg && <p className="text-success">{successMsg}</p>}

          <div className="d-flex justify-content-end mt-3">
            <Button
              onClick={handleSave}
              variant="success"
              disabled={isLoading}
              className="me-2"
            >
              <FaCheck className="me-1" size={12} />
              {isLoading ? t("saving") : t("save")}
            </Button>

            <Button
              onClick={closeProfileForm}
              className="form-close-button"
              disabled={isLoading}
            >
              <FaTimes className="me-2" size={12} />
              {t("close")}
            </Button>
          </div>
        </Form>
      </Modal.Body>
      {isLoading && <FullPageLoader />}
    </Modal>
  );
};

export default Profile;
